import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Activity } from 'lucide-react';

interface MonitoringFallbackUIProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
  showRetryButton?: boolean;
  connectionStatus?: 'connected' | 'disconnected' | 'disabled';
}

export const MonitoringFallbackUI = ({
  title = "Real-time monitoring unavailable",
  description = "Using cached data. Connection failed after multiple attempts.",
  onRetry,
  showRetryButton = true,
  connectionStatus = 'disabled'
}: MonitoringFallbackUIProps) => {
  const getStatusConfig = () => {
    switch (connectionStatus) {
      case 'connected':
        return {
          icon: Activity,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          title: 'Monitoring Active',
          description: 'Real-time data is being received.'
        };
      case 'disconnected':
        return {
          icon: RefreshCw,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          title: 'Reconnecting...',
          description: 'Attempting to restore real-time connection.'
        };
      default:
        return {
          icon: AlertTriangle,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          title,
          description
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  if (connectionStatus === 'connected') {
    return null; // Don't show fallback when connected
  }

  return (
    <Card className={`${config.bgColor} ${config.borderColor}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon className={`h-5 w-5 ${config.color}`} />
            <div>
              <h3 className={`text-sm font-medium ${config.color}`}>
                {config.title}
              </h3>
              <p className={`text-sm ${config.color.replace('600', '700')}`}>
                {config.description}
              </p>
            </div>
          </div>
          {showRetryButton && onRetry && connectionStatus === 'disabled' && (
            <Button
              onClick={onRetry}
              variant="outline"
              size="sm"
              className={`${config.color} border-current hover:bg-current hover:text-white`}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry Connection
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};